import type { Scene, MapAdapter } from '../types';

const TITILER = 'https://titiler-centinela-746476722093.us-central1.run.app';

// uint8 COG — nodata=0 clips the ocean/nodata pixels; nodata=nan causes 500 on integer types
const CANOPY_URL =
  `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
  `?url=${encodeURIComponent('gs://centinela-test-data/openclaw/canopy-height/alsgedi_v6/alsgedi_canopy_height_v6_cog.tif')}` +
  `&bidx=1&rescale=0,25&colormap_name=ylgn&resampling=bilinear`;

// SOC density (raw 40–80 rescaled to mg/cm³) — display as dg/cm³ (÷100) → 0.40–0.80
const SOC_URL =
  `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
  `?url=${encodeURIComponent('gs://centinela-test-data/openclaw/soil/soc_sapi_2021_0_30cm_cog.tif')}` +
  `&bidx=1&rescale=40,80&colormap_name=ylorbr`;

interface LayerDef {
  id:       string;
  label:    string;
  sub:      string;
  desc:     string;
  url:      string;
  disabled: boolean;
  legend:   { color: string; label: string }[];
}

const LAYERS: LayerDef[] = [
  {
    id:       'canopy-height',
    label:    'Canopy Height',
    sub:      'ALSGEDI v6 · 1 m · Meta / Data for Good',
    desc:     'Canopy height in metres (0–32 m). Derived from GEDI lidar fused with Landsat imagery. Reveals the structural complexity of forest and woodland canopy across the concession.',
    url:      CANOPY_URL,
    disabled: false,
    legend:   [
      { color: '#ffffe5', label: '0 m'  },
      { color: '#addd8e', label: '5 m'  },
      { color: '#41ab5d', label: '10 m' },
      { color: '#006837', label: '32 m' },
    ],
  },
  {
    id:       'soc-current',
    label:    'Soil Carbon',
    sub:      'SOC density · 0–30 cm · 2021 · Sapi',
    desc:     'Soil organic carbon density (dg/cm³) at 0–30 cm depth, Sapi 2021. ' +
              'Higher values indicate greater topsoil carbon stocks — the baseline for tracking sequestration outcomes.',
    url:      SOC_URL,
    disabled: false,
    legend:   [
      { color: '#ffffd4', label: '0.40 dg/cm³' },
      { color: '#fe9929', label: '0.60 dg/cm³' },
      { color: '#993404', label: '0.80 dg/cm³' },
    ],
  },
];

type LayerId = string;

// ── Palette matching LOCATIONS ──────────────────────────────────────────────
const PROP_COLORS: Record<string, string> = {
  'Sapi':       '#D4B346',   // gold — our study area
  'Mana Pools': '#97B8CB',   // blue
  'Chewore':    '#3F8533',   // green
};

// ── SVG helpers ─────────────────────────────────────────────────────────────
function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

// ── CSV parsing ─────────────────────────────────────────────────────────────
interface ChartRow { property: string; year: number; value: number; }

function parseCsv(csv: string, valueCol: string): ChartRow[] {
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
  const propIdx  = headers.indexOf('property');
  const yearIdx  = headers.indexOf('year');
  const valIdx   = headers.indexOf(valueCol.toLowerCase());
  if (propIdx < 0 || yearIdx < 0 || valIdx < 0) return [];
  return lines.slice(1).map(line => {
    const cols = line.split(',');
    return {
      property: cols[propIdx]?.trim().replace(/^"|"$/g, '') ?? '',
      year:     parseInt(cols[yearIdx]),
      value:    parseFloat(cols[valIdx]),
    };
  }).filter(r => r.property && isFinite(r.year) && isFinite(r.value));
}

/** Average multiple rows that share the same property+year (e.g. multi-sample inventory). */
function averageByYear(rows: ChartRow[]): ChartRow[] {
  const groups = new Map<string, number[]>();
  rows.forEach(r => {
    const key = `${r.property}|${r.year}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r.value);
  });
  return [...groups.entries()].map(([key, vals]) => {
    const [property, yr] = key.split('|');
    return { property, year: parseInt(yr), value: vals.reduce((a, b) => a + b, 0) / vals.length };
  });
}

// ── Chart render ─────────────────────────────────────────────────────────────
function buildChart(
  rows: ChartRow[], yLabel: string, tooltipLabel: string, unit: string,
  xTickStep = 1,
  secondaryRows?: ChartRow[], secondaryYLabel?: string, secondaryUnit?: string,
  secYMin?: number, secYMax?: number,
  yDecimals = 0,
): SVGSVGElement {
  const hasSecondary = (secondaryRows?.length ?? 0) > 0;
  const W = 420, H = 160, PL = 38, PR = hasSecondary ? 42 : 12, PT = 14, PB = 28;
  const INNER_W = W - PL - PR;
  const INNER_H = H - PT - PB;

  const properties = [...new Set(rows.map(r => r.property))].sort();
  const years      = [...new Set(rows.map(r => r.year))].sort((a, b) => a - b);
  const allV       = rows.map(r => r.value);
  const span       = Math.max(...allV) - Math.min(...allV);
  const yMin       = Math.floor(Math.min(...allV) - span * 0.05);
  const yMax       = Math.ceil(Math.max(...allV)  + span * 0.05);

  const xScale  = (year: number) => PL + ((year - years[0]) / (years[years.length - 1] - years[0])) * INNER_W;
  const yScale  = (v: number)    => PT + INNER_H - ((v - yMin) / (yMax - yMin)) * INNER_H;

  // Secondary axis scale (guard against zero-range data)
  let yMin2 = 0, yMax2 = 1, yScale2 = (_v: number) => PT + INNER_H;
  if (hasSecondary) {
    const allV2  = secondaryRows!.map(r => r.value);
    const span2  = Math.max(...allV2) - Math.min(...allV2);
    const pad2   = span2 > 0 ? span2 * 0.05 : 0.5;
    yMin2  = secYMin !== undefined ? secYMin : Math.min(...allV2) - pad2;
    yMax2  = secYMax !== undefined ? secYMax : Math.max(...allV2) + pad2;
    yScale2 = (v: number) => PT + INNER_H - ((v - yMin2) / (yMax2 - yMin2)) * INNER_H;
  }

  const svg = svgEl('svg', {
    viewBox: `0 0 ${W} ${H}`,
    width: String(W), height: String(H),
  });
  svg.style.overflow = 'visible';

  // Grid lines + left Y labels
  const yTicks = 4;
  for (let i = 0; i <= yTicks; i++) {
    const v = yMin + (i / yTicks) * (yMax - yMin);
    const y = yScale(v);
    svg.appendChild(svgEl('line', {
      x1: String(PL), x2: String(W - PR), y1: String(y), y2: String(y),
      stroke: 'rgba(255,255,255,0.05)', 'stroke-width': '0.5',
    }));
    const t = svgEl('text', {
      x: String(PL - 4), y: String(y),
      'text-anchor': 'end', 'dominant-baseline': 'middle',
      'font-size': '7', fill: '#6b8091',
    });
    t.textContent = v.toFixed(yDecimals);
    svg.appendChild(t);
  }

  // Right Y axis ticks + labels (secondary)
  if (hasSecondary) {
    for (let i = 0; i <= yTicks; i++) {
      const v  = yMin2 + (i / yTicks) * (yMax2 - yMin2);
      const y  = yScale2(v);
      const t  = svgEl('text', {
        x: String(W - PR + 4), y: String(y),
        'text-anchor': 'start', 'dominant-baseline': 'middle',
        'font-size': '7', fill: '#6b8091',
      });
      t.textContent = v.toFixed(1);
      svg.appendChild(t);
    }
    // Right axis label
    const r2LabelEl = svgEl('text', {
      x: String(W - 6), y: String(PT + INNER_H / 2),
      'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091',
      transform: `rotate(90, ${W - 6}, ${PT + INNER_H / 2})`,
    });
    r2LabelEl.textContent = secondaryYLabel ?? '';
    svg.appendChild(r2LabelEl);
  }

  // X axis labels — only show ticks at every xTickStep years
  years.filter(yr => (yr - years[0]) % xTickStep === 0).forEach(yr => {
    const x = xScale(yr);
    const t = svgEl('text', {
      x: String(x), y: String(H - PB + 10),
      'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091',
    });
    t.textContent = String(yr);
    svg.appendChild(t);
  });

  // Left Y axis label
  const yLabelEl = svgEl('text', {
    x: String(8), y: String(PT + INNER_H / 2),
    'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091',
    transform: `rotate(-90, 8, ${PT + INNER_H / 2})`,
  });
  yLabelEl.textContent = yLabel;
  svg.appendChild(yLabelEl);

  // Primary lines per property (solid)
  properties.forEach(prop => {
    const propRows = rows.filter(r => r.property === prop).sort((a, b) => a.year - b.year);
    const pts = propRows.map(r => `${xScale(r.year).toFixed(1)},${yScale(r.value).toFixed(1)}`).join(' ');
    const color  = PROP_COLORS[prop] ?? '#ffffff';
    const isSapi = prop === 'Sapi';
    svg.appendChild(svgEl('polyline', {
      points: pts, fill: 'none',
      stroke: color,
      'stroke-width': isSapi ? '2.2' : '1.4',
      opacity: isSapi ? '1' : '0.55',
    }));
  });

  // Secondary lines per property (dotted, same colors, secondary y-scale)
  if (hasSecondary) {
    properties.forEach(prop => {
      const propRows = secondaryRows!.filter(r => r.property === prop).sort((a, b) => a.year - b.year);
      if (!propRows.length) return;
      const pts = propRows.map(r => `${xScale(r.year).toFixed(1)},${yScale2(r.value).toFixed(1)}`).join(' ');
      const color  = PROP_COLORS[prop] ?? '#ffffff';
      const isSapi = prop === 'Sapi';
      svg.appendChild(svgEl('polyline', {
        points: pts, fill: 'none',
        stroke: color,
        'stroke-width': isSapi ? '2.0' : '1.2',
        'stroke-dasharray': '3 2',
        opacity: isSapi ? '0.85' : '0.45',
      }));
    });
  }

  // Crosshair
  const xhair = svgEl('line', {
    y1: String(PT), y2: String(H - PB),
    stroke: 'rgba(212,179,70,0.6)', 'stroke-width': '1',
  });
  xhair.style.display = 'none';
  svg.appendChild(xhair);

  // Dot markers (primary axis)
  const dots: Map<string, SVGCircleElement> = new Map();
  properties.forEach(prop => {
    const color = PROP_COLORS[prop] ?? '#ffffff';
    const dot = svgEl('circle', {
      r: '3', fill: color, stroke: '#0a121a', 'stroke-width': '1.2',
    });
    dot.style.display = 'none';
    svg.appendChild(dot);
    dots.set(prop, dot);
  });

  // Tooltip
  const tip = document.createElement('div');
  tip.className = 'sm-bii-ref-tooltip';
  tip.style.display = 'none';
  document.body.appendChild(tip);

  // Hover zone
  const zone = svgEl('rect', {
    x: String(PL), y: String(PT),
    width: String(INNER_W), height: String(INNER_H),
    fill: 'transparent',
  });
  zone.style.cursor = 'crosshair';
  svg.appendChild(zone);

  zone.addEventListener('mousemove', (e) => {
    const rect  = svg.getBoundingClientRect();
    const xFrac = Math.max(0, Math.min(1, (e.clientX - rect.left - PL) / INNER_W));
    const yr    = years[0] + xFrac * (years[years.length - 1] - years[0]);
    const nearestYr = years.reduce((a, b) => Math.abs(b - yr) < Math.abs(a - yr) ? b : a);
    const x = xScale(nearestYr);
    xhair.setAttribute('x1', String(x)); xhair.setAttribute('x2', String(x));
    xhair.style.display = '';

    const tipRows = properties.map(prop => {
      const row  = rows.find(r => r.property === prop && r.year === nearestYr);
      const row2 = secondaryRows?.find(r => r.property === prop && r.year === nearestYr);
      const v    = row?.value;
      const v2   = row2?.value;
      const color = PROP_COLORS[prop] ?? '#fff';
      const dot   = dots.get(prop)!;
      if (v !== undefined) {
        dot.setAttribute('cx', String(x));
        dot.setAttribute('cy', String(yScale(v)));
        dot.style.display = '';
      } else {
        dot.style.display = 'none';
      }
      const v2Str = v2 !== undefined ? ` · ${v2.toFixed(2)} ${secondaryUnit ?? ''}` : '';
      return `<div style="display:flex;justify-content:space-between;gap:12px;">
        <span style="color:${color}">${prop}</span>
        <span>${v !== undefined ? v.toFixed(yDecimals) : '—'} ${unit}${v2Str}</span>
      </div>`;
    }).join('');
    tip.innerHTML = `<div style="font-weight:600;margin-bottom:4px;">${nearestYr} · ${tooltipLabel}</div>${tipRows}`;
    tip.style.display = 'block';
    tip.style.left = `${e.pageX + 14}px`;
    tip.style.top  = `${e.pageY - 10}px`;
    const r = tip.getBoundingClientRect();
    if (r.right > window.innerWidth - 16) tip.style.left = `${e.pageX - r.width - 14}px`;
  });

  zone.addEventListener('mouseleave', () => {
    xhair.style.display = 'none';
    tip.style.display = 'none';
    dots.forEach(d => { d.style.display = 'none'; });
  });

  return svg as unknown as SVGSVGElement;
}

// ── Scene class ─────────────────────────────────────────────────────────────

export class SceneCarbonBaseline implements Scene {
  readonly panel: HTMLElement;
  private refTip: HTMLElement | null = null;
  private mapLegend: HTMLElement | null = null;
  private built = false;
  private activeLayer: LayerId = 'canopy-height';
  private readonly biomassRows: ChartRow[];
  private readonly soilRows:    ChartRow[];
  private readonly ocPctRows:   ChartRow[];
  private readonly gppRows:     ChartRow[];

  constructor(
    root: HTMLElement,
    private readonly map: MapAdapter,
    soilCsv    = '',
    gppCsv     = '',
    biomassCsv = '',
  ) {
    this.biomassRows = averageByYear(parseCsv(biomassCsv, 'MgC_per_ha')).map(r => ({ ...r, value: r.value * 0.5 }));
    this.soilRows    = parseCsv(soilCsv, 'oc_mg_cm3_mean').map(r => ({ ...r, value: r.value / 10 }));
    this.ocPctRows   = parseCsv(soilCsv, 'oc_wpct_mean');
    this.gppRows     = parseCsv(gppCsv,  'gpp_gc_m2_mean');

    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'carbon-baseline';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Carbon Baseline</div>
        <div class="sm-panel-sub">Biodiversity · Canopy · Soil Carbon · Sapi Safari Area</div>
      </div>

      <div class="sm-headline">
        Sapi's forests and savannas represent one of the most intact carbon landscapes
        in sub-Saharan Africa — a measured baseline before any intervention.
      </div>

      <div class="sm-carbon-two-col">

        <div class="sm-carbon-left">

          <div class="sm-bii-block">
            <div class="sm-bii-header">
              <div class="sm-bii-label">Biodiversity Intactness Index · Sapi Study Area</div>
              <span class="sm-bii-ref-icon" id="sm-bii-ref-icon">?</span>
            </div>
            <div class="sm-bii-scale">
              <div class="sm-bii-scale-track">
                <div class="sm-bii-ssa-tick" style="left:74.3%">
                  <span class="sm-bii-ssa-label">SSA mean</span>
                </div>
              </div>
              <div class="sm-bii-scale-anchors">
                <span class="sm-bii-anchor-left">&lt;55%<br>Rwanda / Nigeria</span>
                <span class="sm-bii-anchor-right">85%<br>Namibia / Botswana</span>
              </div>
            </div>
            <div class="sm-bii-bars">
              <div class="sm-bii-bar-row">
                <span class="sm-bii-bar-label">All species</span>
                <div class="sm-bii-bar-track">
                  <div class="sm-bii-bar-fill sm-bii-bar-all" style="width:100%"></div>
                </div>
                <span class="sm-bii-bar-value">0.87</span>
              </div>
              <div class="sm-bii-bar-row">
                <span class="sm-bii-bar-label">Birds</span>
                <div class="sm-bii-bar-track">
                  <div class="sm-bii-bar-fill sm-bii-bar-birds" style="width:88.7%"></div>
                </div>
                <span class="sm-bii-bar-value">0.81</span>
              </div>
              <div class="sm-bii-bar-row">
                <span class="sm-bii-bar-label">Mammals</span>
                <div class="sm-bii-bar-track">
                  <div class="sm-bii-bar-fill sm-bii-bar-mammals" style="width:77.7%"></div>
                </div>
                <span class="sm-bii-bar-value">0.77</span>
              </div>
            </div>
            <div class="sm-bii-desc">
              Sapi is almost completely intact, with only distant fragmentations present
              from highways to the west and some agriculture over the border of Chewore
              in Mozambique.
            </div>
          </div>

          <div class="sm-carbon-layers">
            <div class="sm-carbon-layers-label">Map Layer</div>
            <div class="sm-carbon-layer-btns" id="sm-carbon-layer-btns">
              ${LAYERS.map(l => `
                <button class="sm-carbon-layer-btn${l.id === 'canopy-height' ? ' active' : ''}${l.disabled ? ' disabled' : ''}"
                        data-layer="${l.id}"
                        ${l.disabled ? 'disabled title="Layer data not yet available"' : ''}>${l.label}</button>`).join('')}
            </div>
            <div class="sm-carbon-layer-info" id="sm-carbon-layer-info">
              <div class="sm-carbon-layer-sub" id="sm-carbon-layer-sub"></div>
              <div class="sm-carbon-legend" id="sm-carbon-legend"></div>
              <div class="sm-carbon-layer-desc" id="sm-carbon-layer-desc"></div>
            </div>
          </div>

        </div>

        <div class="sm-carbon-right">
          <div class="sm-carbon-chart-label">Above-Ground Biomass Carbon · MgC/ha · 2021–2025</div>
          <div id="sm-biomass-chart-wrap"></div>
          <div class="sm-carbon-chart-legend" id="sm-biomass-chart-legend"></div>
          <div class="sm-carbon-chart-note">
            Mean above-ground biomass carbon (MgC/ha) across Sapi, Mana Pools and Chewore,
            downscaled from WDPA protected-area inventories. Sapi's escarpment woodland
            consistently holds the highest stocks.
          </div>

          <div class="sm-carbon-chart-label" style="margin-top:14px;">Soil Organic Carbon · 0–30 cm · 2021 · dg/cm³</div>
          <div id="sm-carbon-chart-wrap"></div>
          <div class="sm-carbon-chart-legend" id="sm-carbon-chart-legend"></div>
          <div class="sm-carbon-chart-note">
            SOC inventory (dg/cm³) across Sapi, Mana Pools and Chewore.
            Year-to-year variation reflects rainfall-driven changes in active carbon pools.
          </div>

          <div class="sm-carbon-chart-label" style="margin-top:14px;">
            Gross Primary Productivity · gC/m² · 2000–2024
          </div>
          <div id="sm-gpp-chart-wrap"></div>
          <div class="sm-carbon-chart-legend" id="sm-gpp-chart-legend"></div>
          <div class="sm-carbon-chart-note">
            Annual GPP (gC/m²) from MODIS MOD17A3HGF. Sapi's escarpment
            gradient drives higher productivity in highland woodland versus
            lowland Mopane.
          </div>
        </div>

      </div>`;

    root.appendChild(this.panel);

    // Floating map legend — appended to the map container, shown/updated by showLayer()
    this.mapLegend = document.createElement('div');
    this.mapLegend.className = 'sm-map-layer-legend';
    this.mapLegend.style.display = 'none';
    document.getElementById('sm-map')?.appendChild(this.mapLegend);
  }

  onEnter(): void {
    if (!this.built) {
      this.buildControls();
      this.built = true;
    }
    this.showLayer(this.activeLayer);
  }

  onLeave(): void {
    LAYERS.forEach(l => this.map.removeRasterSource(l.id));
    if (this.mapLegend) this.mapLegend.style.display = 'none';
  }

  private buildControls(): void {
    // Layer toggle buttons
    const btns = this.panel.querySelector<HTMLElement>('#sm-carbon-layer-btns')!;
    btns.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.sm-carbon-layer-btn');
      if (!btn) return;
      const id = btn.dataset['layer'] as LayerId;
      btns.querySelectorAll('.sm-carbon-layer-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      this.showLayer(id);
    });

    // BII reference tooltip
    const refIcon = this.panel.querySelector<HTMLElement>('#sm-bii-ref-icon')!;
    this.refTip = document.createElement('div');
    this.refTip.className = 'sm-bii-ref-tooltip';
    this.refTip.innerHTML =
      '<strong>Clements et al. (2025) Nature</strong><br>' +
      '"Reassessing Africa\'s biodiversity intactness" — pixel-level BII derived from ' +
      'PREDICTS occupancy models, land-use and human pressure data across sub-Saharan Africa. ' +
      'Values represent the fraction of original species abundance predicted to remain ' +
      'relative to a minimally-disturbed baseline.';
    this.refTip.style.display = 'none';
    document.body.appendChild(this.refTip);

    const positionTip = (e: MouseEvent) => {
      this.refTip!.style.left = `${e.pageX + 12}px`;
      this.refTip!.style.top  = `${e.pageY - 10}px`;
      const r = this.refTip!.getBoundingClientRect();
      if (r.right > window.innerWidth - 16)
        this.refTip!.style.left = `${e.pageX - r.width - 12}px`;
    };
    refIcon.addEventListener('mouseenter', (e) => { this.refTip!.style.display = 'block'; positionTip(e as MouseEvent); });
    refIcon.addEventListener('mousemove',  (e) => positionTip(e as MouseEvent));
    refIcon.addEventListener('mouseleave', () => { this.refTip!.style.display = 'none'; });

    // Helper: build chart + legend pair
    const buildChartBlock = (
      rows: ChartRow[], wrapId: string, legId: string,
      yLabel: string, tipLabel: string, unit: string,
      xTickStep = 1,
      secondaryRows?: ChartRow[], secondaryYLabel?: string, secondaryUnit?: string,
      secYMin?: number, secYMax?: number,
      yDecimals = 0,
    ) => {
      const wrap = this.panel.querySelector<HTMLElement>(`#${wrapId}`)!;
      if (rows.length > 0) {
        wrap.appendChild(buildChart(rows, yLabel, tipLabel, unit, xTickStep, secondaryRows, secondaryYLabel, secondaryUnit, secYMin, secYMax, yDecimals));
      } else {
        wrap.innerHTML = '<div style="font-size:10px;color:#6b8091;padding:8px 0;">Data unavailable</div>';
      }
      const leg   = this.panel.querySelector<HTMLElement>(`#${legId}`)!;
      const props = [...new Set(rows.map(r => r.property))].sort();
      leg.innerHTML = props.map(p => {
        const color  = PROP_COLORS[p] ?? '#fff';
        const isSapi = p === 'Sapi';
        return `<div class="sm-carbon-chart-leg-item">
          <span class="sm-carbon-chart-leg-swatch" style="background:${color};opacity:${isSapi ? 1 : 0.55}"></span>
          <span style="color:${color};opacity:${isSapi ? 1 : 0.75};font-weight:${isSapi ? 600 : 400}">${p}</span>
        </div>`;
      }).join('');
    };

    buildChartBlock(
      this.biomassRows, 'sm-biomass-chart-wrap', 'sm-biomass-chart-legend',
      'MgC/ha', 'Above-Ground Biomass', 'MgC/ha',
    );
    buildChartBlock(
      this.soilRows, 'sm-carbon-chart-wrap', 'sm-carbon-chart-legend',
      'OC dg/cm³', 'SOC 0–30 cm', 'dg/cm³',
      1,
      this.ocPctRows, 'OC wt%', 'wt%',
      0.0, 1.0,
      2,
    );
    buildChartBlock(
      this.gppRows, 'sm-gpp-chart-wrap', 'sm-gpp-chart-legend',
      'gC/m²', 'Gross Primary Productivity', 'gC/m²',
      5,
    );
  }

  private showLayer(id: LayerId): void {
    LAYERS.forEach(l => this.map.removeRasterSource(l.id));
    this.activeLayer = id;
    const meta = LAYERS.find(l => l.id === id)!;
    if (!meta.disabled) this.map.addRasterSource(id, meta.url, 0.82);

    const subEl  = this.panel.querySelector<HTMLElement>('#sm-carbon-layer-sub')!;
    const legEl  = this.panel.querySelector<HTMLElement>('#sm-carbon-legend')!;
    const descEl = this.panel.querySelector<HTMLElement>('#sm-carbon-layer-desc')!;
    subEl.textContent  = meta.sub;
    descEl.textContent = meta.desc;

    const swatchHTML = meta.legend.map(s =>
      `<div class="sm-carbon-swatch">
        <span class="sm-carbon-swatch-color" style="background:${s.color}"></span>
        <span class="sm-carbon-swatch-label">${s.label}</span>
       </div>`
    ).join('');
    legEl.innerHTML = swatchHTML;

    // Update floating map legend
    if (this.mapLegend) {
      if (!meta.disabled && meta.legend.length > 0) {
        this.mapLegend.innerHTML =
          `<div class="sm-map-legend-title">${meta.label}</div>` +
          meta.legend.map(s =>
            `<div class="sm-map-legend-row">
              <span class="sm-map-legend-swatch" style="background:${s.color}"></span>
              <span class="sm-map-legend-label">${s.label}</span>
             </div>`
          ).join('');
        this.mapLegend.style.display = 'block';
      } else {
        this.mapLegend.style.display = 'none';
      }
    }
  }
}
